import './paymentMethodButton.css';
import React, {Component} from "react";
import {createPortal} from "react-dom";
import PaymentMethodService from "../services/paymentMethodService";
import {CompatiblePaymentMethodsResponseDto, PaymentMethodLinkDto} from "../model/functionModels";
import i18nService from "../../i18n/i18nService";

interface PaymentMethodButtonProps {
    targetUserId: string,
    totalDebt: number,
    onPaymentComplete: () => void, // Called when payment link is opened
    disabled?: boolean,
}

interface PaymentMethodButtonState {
    compatibleMethods?: PaymentMethodLinkDto[],
    selectedMethod?: PaymentMethodLinkDto,
    showDropdown: boolean,
    isLoading: boolean,
    dropdownPosition: { top: number; left: number; width: number } | null,
}

class PaymentMethodButton extends Component<PaymentMethodButtonProps, PaymentMethodButtonState> {
    constructor(props: PaymentMethodButtonProps) {
        super(props);
        this.state = {
            showDropdown: false,
            isLoading: false,
            dropdownPosition: null,
        };
        this.handleClick = this.handleClick.bind(this);
        this.handleMethodSelect = this.handleMethodSelect.bind(this);
        this.loadCompatibleMethods = this.loadCompatibleMethods.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        this.loadCompatibleMethods();
        document.addEventListener('mousedown', this.handleClickOutside);
        document.addEventListener('touchstart', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        document.removeEventListener('touchstart', this.handleClickOutside);
    }

    handleClickOutside(event: MouseEvent | TouchEvent) {
        const target = event.target as Node;
        if (this.containerRef && !this.containerRef.contains(target)) {
            // Also check if click is on the dropdown portal
            const dropdown = document.querySelector('.PaymentMethodButton-Dropdown');
            if (dropdown && !dropdown.contains(target)) {
                this.setState({ showDropdown: false, dropdownPosition: null });
            }
        }
    }

    containerRef: HTMLDivElement | null = null;


    async loadCompatibleMethods() {
        this.setState({isLoading: true});
        await PaymentMethodService.getCompatiblePaymentMethods(
            this.props.targetUserId,
            async (response: CompatiblePaymentMethodsResponseDto) => {
                if (response.methods && response.methods.length > 0) {
                    this.setState({
                        compatibleMethods: response.methods,
                        selectedMethod: response.methods[0], // First is highest priority
                        isLoading: false,
                    });
                } else {
                    this.setState({isLoading: false});
                }
            },
            async (error: string) => {
                console.error("Failed to load compatible payment methods:", error);
                this.setState({isLoading: false});
            },
            async () => {}
        );
    }

    handleClick() {
        if (!this.state.selectedMethod) {
            return;
        }
        // Open external payment link
        window.open(this.state.selectedMethod.link, '_blank');
        // Show payment confirmation modal immediately
        this.setState({showDropdown: false, dropdownPosition: null});
        // Trigger payment confirmation modal
        this.props.onPaymentComplete();
    }

    handleMethodSelect(method: PaymentMethodLinkDto) {
        this.setState({selectedMethod: method, showDropdown: false, dropdownPosition: null});
    }

    handleArrowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const willShow = !this.state.showDropdown;
        if (willShow && this.containerRef) {
            const rect = this.containerRef.getBoundingClientRect();
            this.setState({
                showDropdown: true,
                dropdownPosition: {
                    top: rect.bottom + window.scrollY + 4,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                }
            });
        } else {
            this.setState({showDropdown: false, dropdownPosition: null});
        }
    }

    getMethodDisplayName(methodType: string): string {
        if (methodType === "REVOLUT_ME") {
            return "Revolut";
        } else if (methodType === "PAYPAL_ME") {
            return "PayPal";
        }
        return methodType;
    }

    getMethodIcon(methodType: string): React.ReactNode {
        if (methodType === "REVOLUT_ME") {
            return (
                <img 
                    src="/logos/Revolut.svg" 
                    alt="Revolut" 
                    className="PaymentMethodButton-Icon"
                />
            );
        } else if (methodType === "PAYPAL_ME") {
            return (
                <img 
                    src="/logos/Paypal.svg" 
                    alt="PayPal" 
                    className="PaymentMethodButton-Icon"
                />
            );
        }
        return null;
    }

    render() {
        if (!this.state.compatibleMethods || this.state.compatibleMethods.length === 0) {
            return null; // Don't show button if no compatible methods
        }

        const hasMultipleMethods = this.state.compatibleMethods.length > 1;
        const methodName = this.state.selectedMethod ? this.getMethodDisplayName(this.state.selectedMethod.methodType) : "";

        return (
            <div className="PaymentMethodButton-Container" ref={(el) => this.containerRef = el}>
                <div className="PaymentMethodButton-ButtonGroup">
                    <button
                        className="PaymentMethodButton-Button"
                        onClick={this.handleClick}
                        disabled={this.props.disabled || this.state.isLoading || !this.state.selectedMethod}
                    >
                        {this.state.isLoading ? (
                            i18nService.t('common.loading')
                        ) : (
                            this.state.selectedMethod && this.getMethodIcon(this.state.selectedMethod.methodType)
                        )}
                    </button>
                    {hasMultipleMethods && (
                        <button
                            className="PaymentMethodButton-ArrowButton"
                            onClick={this.handleArrowClick}
                            disabled={this.props.disabled || this.state.isLoading}
                            aria-label="Select payment method"
                        >
                            <span className="PaymentMethodButton-DropdownArrow">▼</span>
                        </button>
                    )}
                </div>
                {hasMultipleMethods && this.state.showDropdown && this.state.dropdownPosition && createPortal(
                    <div 
                        className="PaymentMethodButton-Dropdown"
                        style={{
                            position: 'absolute',
                            top: `${this.state.dropdownPosition.top}px`,
                            left: `${this.state.dropdownPosition.left}px`,
                            minWidth: `${this.state.dropdownPosition.width}px`,
                        }}
                    >
                        {this.state.compatibleMethods.map((method, index) => (
                            <button
                                key={index}
                                className={`PaymentMethodButton-DropdownItem ${this.state.selectedMethod === method ? 'PaymentMethodButton-DropdownItemActive' : ''}`}
                                onClick={() => this.handleMethodSelect(method)}
                            >
                                <span className="PaymentMethodButton-DropdownItemIcon">{this.getMethodIcon(method.methodType)}</span>
                                <span>{this.getMethodDisplayName(method.methodType)}</span>
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
            </div>
        );
    }
}

export default PaymentMethodButton;

